import { io } from "socket.io-client";

class ChatSocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.listeners = new Map();
    this.currentUser = null;
    this.url = null;
    this.config = null;
  }

  async loadConfig() {
    try {
      const response = await fetch("/config.json");
      if (!response.ok) {
        throw new Error("Failed to load config");
      }
      this.config = await response.json();
      this.url = this.config.liveChatUrl || "http://localhost:5000";
      return this.config;
    } catch (error) {
      console.error("Error loading config:", error);
      // Fallback to default
      this.url = "http://localhost:5000";
      return null;
    }
  }

  /**
   * Initialize socket connection
   */
  async connect(serverUrl = null) {
    if (this.socket && this.isConnected) {
      return this.socket;
    }

    if (!this.config) {
      await this.loadConfig();
    }

    const connectionUrl = serverUrl || this.url || "http://localhost:5000";

    console.log("Connecting to socket server:", connectionUrl);

    this.socket = io(connectionUrl, {
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    this.setupEventHandlers();
    return this.socket;
  }

  /**
   * Setup socket event handlers
   */
  setupEventHandlers() {
    this.socket.on("connect", () => {
      console.log("Socket connected:", this.socket.id);
      this.isConnected = true;
      this.triggerEvent("connect");
    });

    this.socket.on("disconnect", () => {
      console.log("Socket disconnecteds");
      this.isConnected = false;
      this.triggerEvent("disconnect");
    });

    this.socket.on("connect_error", (error) => {
      console.error("Socket connection error:", error);
      this.triggerEvent("connect_error", error);
    });

    // Chat event handlers that match backend implementation
    this.socket.on("chat-message", (data) => {
      this.triggerEvent("chat-message", data);
    });

    this.socket.on("user-typing", (data) => {
      this.triggerEvent("user-typing", data);
    });

    this.socket.on("messages-read", (data) => {
      this.triggerEvent("messages-read", data);
    });

    this.socket.on("voice-call-invite", (data) => {
      this.triggerEvent("voice-call-invite", data);
    });

    this.socket.on("voice-call-response", (data) => {
      this.triggerEvent("voice-call-response", data);
    });

    this.socket.on("voice-call-start", (data) => {
      this.triggerEvent("voice-call-start", data);
    });

    this.socket.on("user-joined-chat", (data) => {
      this.triggerEvent("user-joined-chat", data);
    });

    this.socket.on("chat-conversation-data", (data) => {
      this.triggerEvent("chat-conversation-data", data);
    });

    this.socket.on("chat-error", (data) => {
      this.triggerEvent("chat-error", data);
    });

    // WebRTC event handlers
    this.socket.on("offer", (data) => {
      this.triggerEvent("offer", data);
    });

    this.socket.on("answer", (data) => {
      this.triggerEvent("answer", data);
    });

    this.socket.on("ice-candidate", (data) => {
      this.triggerEvent("ice-candidate", data);
    });

    this.socket.on("call-ready", (data) => {
      this.triggerEvent("call-ready", data);
    });

    this.socket.on("call-ended", (data) => {
      this.triggerEvent("call-ended", data);
    });
  }

  /**
   * Authenticate with the server
   */
  authenticate(userData) {
    if (!this.socket) {
      throw new Error("Socket not connected");
    }

    this.currentUser = userData;
    this.socket.emit("authenticate", userData);
  }

  /**
   * Simulate triggering events for demo
   */
  triggerEvent(eventName, data) {
    const callback = this.listeners.get(eventName);
    if (callback) {
      callback(data);
    }
  }

  /**
   * Join a chat conversation
   */
  joinChat(ticketId, userId, role, name) {
    if (!this.socket) {
      throw new Error("Socket not connected");
    }

    const data = { ticketId, userId, role, name };

    // Use join-call to set user properties on socket, then join-chat for chat-specific setup
    this.socket.emit("join-call", data);
    this.socket.emit("join-chat", data);

    console.log("Joining chat with user data:", data);
  }

  /**
   * Leave a chat conversation
   */
  leaveChat() {
    if (!this.socket) {
      throw new Error("Socket not connected");
    }

    this.socket.emit("leave-call");
    console.log("Leaving chat");
  }

  /**
   * Send a chat message
   */
  sendMessage(messageData) {
    if (!this.socket) {
      throw new Error("Socket not connected");
    }

    this.socket.emit("chat-message", messageData);
    console.log("Sending message:", messageData);
  }

  /**
   * Send typing indicator
   */
  sendTyping(ticketId, isTyping = true) {
    if (!this.socket) {
      throw new Error("Socket not connected");
    }

    const data = { ticketId, isTyping };
    this.socket.emit("typing", data);
    console.log("Sending typing:", data);
  }

  /**
   * Mark messages as read
   */
  markAsRead(ticketId, messageIds) {
    if (!this.socket) {
      throw new Error("Socket not connected");
    }

    const data = { ticketId, messageIds };
    this.socket.emit("mark-as-read", data);
    console.log("Marking as read:", data);
  }

  /**
   * Send voice call invitation
   */
  sendVoiceCallInvite(callData) {
    if (!this.socket) {
      throw new Error("Socket not connected");
    }

    this.socket.emit("voice-call-invite", callData);
    console.log("Sending voice call invite:", callData);
  }

  /**
   * Respond to voice call invitation
   */
  respondToVoiceCall(responseData) {
    if (!this.socket) {
      throw new Error("Socket not connected");
    }

    this.socket.emit("voice-call-response", responseData);
    console.log("Responding to voice call:", responseData);
  }

  onConnect(callback) {
    this.listeners.set("connect", callback);
    console.log("Socket Connected");
  }

  /**
   * Listen for incoming messages
   */
  onMessage(callback) {
    this.listeners.set("chat-message", callback);
    console.log("Listening for messages");
  }

  /**
   * Listen for typing indicators
   */
  onTyping(callback) {
    this.listeners.set("user-typing", callback);
    console.log("Listening for typing");
  }

  /**
   * Listen for read receipts
   */
  onMessagesRead(callback) {
    this.listeners.set("messages-read", callback);
    console.log("Listening for read receipts");
  }

  /**
   * Listen for voice call invitations
   */
  onVoiceCallInvite(callback) {
    this.listeners.set("voice-call-invite", callback);
    console.log("Listening for voice call invites");
  }

  /**
   * Listen for voice call responses
   */
  onVoiceCallResponse(callback) {
    this.listeners.set("voice-call-response", callback);
    console.log("Listening for voice call responses");
  }

  /**
   * Listen for voice call start
   */
  onVoiceCallStart(callback) {
    this.listeners.set("voice-call-start", callback);
    console.log("Listening for voice call start");
  }

  /**
   * Listen for user joined chat
   */
  onUserJoinedChat(callback) {
    this.listeners.set("user-joined-chat", callback);
    console.log("Listening for user joined chat");
  }

  /**
   * Listen for chat conversation data
   */
  onChatConversationData(callback) {
    this.listeners.set("chat-conversation-data", callback);
    console.log("Listening for chat conversation data");
  }

  /**
   * Listen for chat errors
   */
  onChatError(callback) {
    this.listeners.set("chat-error", callback);
    console.log("Listening for chat errors");
  }

  /**
   * WebRTC Event Listeners
   */
  onOffer(callback) {
    this.listeners.set("offer", callback);
    console.log("Listening for WebRTC offers");
  }

  onAnswer(callback) {
    this.listeners.set("answer", callback);
    console.log("Listening for WebRTC answers");
  }

  onIceCandidate(callback) {
    this.listeners.set("ice-candidate", callback);
    console.log("Listening for ICE candidates");
  }

  onCallReady(callback) {
    this.listeners.set("call-ready", callback);
    console.log("Listening for call ready events");
  }

  onCallEnded(callback) {
    this.listeners.set("call-ended", callback);
    console.log("Listening for call ended events");
  }

  /**
   * WebRTC Emitters
   */
  sendOffer(offerData) {
    if (!this.socket) {
      throw new Error("Socket not connected");
    }
    this.socket.emit("offer", offerData);
    console.log("Sending WebRTC offer:", offerData);
  }

  sendAnswer(answerData) {
    if (!this.socket) {
      throw new Error("Socket not connected");
    }
    this.socket.emit("answer", answerData);
    console.log("Sending WebRTC answer:", answerData);
  }

  sendIceCandidate(candidateData) {
    if (!this.socket) {
      throw new Error("Socket not connected");
    }
    this.socket.emit("ice-candidate", candidateData);
    console.log("Sending ICE candidate:", candidateData);
  }

  endCall() {
    if (!this.socket) {
      throw new Error("Socket not connected");
    }
    this.socket.emit("call-end");
    console.log("Ending call");
  }

  /**
   * Remove event listener
   */
  off(eventName) {
    this.listeners.delete(eventName);
    console.log("Removed listener:", eventName);
  }

  /**
   * Disconnect socket
   */
  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.isConnected = false;
    this.listeners.clear();
    this.currentUser = null;
    console.log("Socket disconnected");
  }

  /**
   * Get connection status
   */
  getConnectionStatus() {
    return this.isConnected;
  }

  /**
   * Get current socket instance
   */
  getSocket() {
    return this.socket;
  }

  /**
   * Get current user data
   */
  getCurrentUser() {
    return this.currentUser;
  }
}

// Create and export singleton instance
const socketService = new ChatSocketService();
export default socketService;
